import Chart from 'react-apexcharts'
import useTwinTranslation from '../../../../utils/hooks/useTwinTranslation'
import { useCallback, useEffect, useState } from 'react'
import { twinFetchPostJSON } from '../../../../utils/globals/data'
import { getISOCurrencyMain } from '../../../../utils/reducers/getters'
import { preciseDecimalSum } from '@teinor/erp/prices'
import { getCurrencySymbol } from '../../../../utils/globals/price'
import { SellTicketMultipleChartData } from './types'
import useDictMonths from '../../../../utils/hooks/useDictMonths'
import { TwinDictionary } from '../../../../utils/globals/dictionary'
import { displayFormatedNumber } from '../../../../utils/globals/numbers'
import { SellTicketStatus, sellTicketStatus } from '@teinor/erp/types/company/order/sell/sellTicket'
import './sellticketmultiplechartdata.sass'

interface DSBSellTicketMultipleChartDataProps extends DSBSellTicketMultipleChartDataLogic {
    title?: string
}

const DSBSellTicketMultipleChartData = ({ title, end_date, start_date }: DSBSellTicketMultipleChartDataProps) => {
    const { t } = useTwinTranslation()
    const { sellTicket } = useDSBSellTicketMultipleChartDataLogic({ end_date, start_date })
    const mainCurrency = getISOCurrencyMain()
    const currencySymbol = getCurrencySymbol(mainCurrency)
    const { arrMonthShort } = useDictMonths()
    const customlegendFormatter = (name: any, opts: any) => {
        return `
            <div class='legend-label'>${name}</div>
            <div class='legend-value'>${displayFormatedNumber(opts.w.globals.seriesTotals[opts.seriesIndex].toFixed(2))}${currencySymbol}</div>
        `
    }
    const series = [
        {
            name: t('reserved', 'Reservadas'),
            type: 'column',
            data: sellTicket.saved
        },
        {
            name: t('effective', 'Efectivas'),
            type: 'column',
            data: sellTicket.accepted
        },
        {
            name: t('refundedsF', 'Devueltas'),
            type: 'column',
            data: sellTicket.refunded
        },
        {
            name: t('totalSells', 'Total ventas'),
            type: 'area',
            data: sellTicket.totals
        },
    ]
    return (
        <div className='flex flex-col gap-10 flex-auto h-1 sell_ticket_multiplechart_data'>
            <h3>{title}</h3>
            <div className='flex-auto h-1 flex flex-col'>
                <div className='flex-auto'>
                    <Chart options={{
                        chart: {
                            type: 'line',
                            stacked: false,
                            events: {
                                mounted: (chart) => {
                                    chart.windowResizeHandler()
                                }
                            },

                            toolbar: {
                                show: false
                            },
                            zoom: {
                                enabled: true
                            }
                        },
                        plotOptions: {
                            bar: {
                                horizontal: false,
                                columnWidth: '60%',
                            },
                        },
                        stroke: {
                            show: true,
                            curve: 'smooth',
                            width: 2,
                        },
                        yaxis: {
                            labels: {
                                style: {
                                    colors: '#8F9BA4',
                                    fontFamily: 'Roboto'
                                }
                            },
                        },
                        xaxis: {
                            labels: {
                                style: {
                                    fontFamily: 'Roboto',
                                    colors: '#8F9BA4',
                                }
                            },
                            categories: arrMonthShort
                        },
                        fill: {
                            opacity: 1,
                        },
                        legend: {
                            position: 'top',
                            horizontalAlign: 'left',
                            formatter: customlegendFormatter,
                            fontFamily: 'Roboto',
                            markers: {
                                offsetY: 3
                            }
                        },
                        colors: ['#e8cf30', '#1DB27B', '#EB2D53', '#DBF1FF'],
                        tooltip: {
                            y: {
                                formatter: function (val) {
                                    return displayFormatedNumber(val.toFixed(2)) + ' ' + currencySymbol
                                }
                            }
                        }
                    }} series={series} type="line" height={'100%'} />
                </div>
            </div>
        </div>
    )
}

interface DSBSellTicketMultipleChartDataLogic {
    start_date: Date
    end_date: Date
}

const useDSBSellTicketMultipleChartDataLogic = ({ end_date, start_date }: DSBSellTicketMultipleChartDataLogic) => {
    const [sellTicket, setSellTicket] = useState<SellTicketMultipleChartData>({ saved: [], savedTotal: 0, accepted: [], acceptedTotal: 0, refunded: [], refundedTotal: 0, totals: [], totalTotal: 0 })

    const getData = useCallback(async () => {
        const result = await twinFetchPostJSON('/api/app/order/tpv/sellTicket/getAllSellTicketsForStats', {
            attributes: ['total_total', 'total_clean_with_vat', 'total_refund_with_vat', 'status', 'createdAt'],
            where: { 'SellTicket.createdAt': { '$betweenN': [start_date, end_date] }, is_last: true }
        })
        const mySellTicketStatus: TwinDictionary = { saved: Array(12).fill(0), savedTotal: 0, accepted: Array(12).fill(0), acceptedTotal: 0, refunded: Array(12).fill(0), refundedTotal: 0, totals: Array(12).fill(0), totalTotal: 0 }


        if (result) {
            for (const element of result) {
                const date = new Date(element.createdAt)
                const month = date.getMonth()
                const myStatusInversed = sellTicketStatus[element.status as keyof SellTicketStatus]
                if (myStatusInversed === 'saved' || myStatusInversed === 'partialCharged') {
                    const mySavedTotal = parseFloat(element.total_clean_with_vat || 0)
                    const myTotalTotal = parseFloat(element.total_total || 0)
                    mySellTicketStatus.saved[month] = preciseDecimalSum(mySellTicketStatus.saved[month], mySavedTotal)
                    mySellTicketStatus.savedTotal = preciseDecimalSum(mySellTicketStatus.savedTotal, mySavedTotal)
                    mySellTicketStatus.totals[month] = preciseDecimalSum(mySellTicketStatus.totals[month], myTotalTotal)
                    mySellTicketStatus.totalTotal = preciseDecimalSum(mySellTicketStatus.totalTotal, myTotalTotal)
                } else {
                    const myAcceptedTotal = parseFloat(element.total_clean_with_vat || 0)
                    const myRefundedTotal = parseFloat(element.total_refund_with_vat || 0)
                    const myTotalTotal = parseFloat(element.total_total || 0)
                    if ( myAcceptedTotal > 0 ) {
                        mySellTicketStatus.accepted[month] = preciseDecimalSum(mySellTicketStatus.accepted[month], myAcceptedTotal)
                        mySellTicketStatus.acceptedTotal = preciseDecimalSum(mySellTicketStatus.acceptedTotal, myAcceptedTotal)
                    }
                    if (myRefundedTotal > 0) {
                        mySellTicketStatus.refunded[month] = preciseDecimalSum(mySellTicketStatus.refunded[month], myRefundedTotal )
                        mySellTicketStatus.refundedTotals = preciseDecimalSum(mySellTicketStatus.refundedTotals, myRefundedTotal )
                    }
                    mySellTicketStatus.totals[month] = preciseDecimalSum(mySellTicketStatus.totals[month], myTotalTotal )
                    mySellTicketStatus.totalTotal = preciseDecimalSum(mySellTicketStatus.totalTotal, myTotalTotal )
                }
            }
            setSellTicket(mySellTicketStatus as SellTicketMultipleChartData)
        }
    }, [setSellTicket, end_date, start_date])


    useEffect(() => {
        getData()
    }, [getData])
    return { sellTicket }
}

export default DSBSellTicketMultipleChartData